import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {emitChangedWait, obsStatuses, obsTypes} from '../obsUtils';
import renderAcceptSection from './renderAcceptSection';
import AgyleBlockSwitch from '../../../../../components/ui/switch/agyleBlockSwitch';
import {obsStyles} from '../obsStyle';

function ObsApnoea(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [apnoea, setApnoea] = React.useState(allowDefaultValue ? props.apnoea : null);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    function setDefaults() {
        setApnoea(false);
    }

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setApnoea(props.apnoea);
        }
    }, []);

    useEffect(() => {
        if ((_.isUndefined(apnoea) || _.isNull(apnoea)) && allowDefaultValue) {
            if (_.isUndefined(props.apnoea) || _.isNull(props.apnoea)) {
                setDefaults();
            } else {
                setApnoea(props.apnoea);
            }
        }
    }, [props.apnoea]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        if (!allowDefaultValue) setApnoea(null);
        else setDefaults();

        clearAccepted();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) setStatus(obsStatuses.completed);
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = (!_.isNull(apnoea) && status === obsStatuses.new) || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) acceptObs();
    }, [apnoea, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    apnoea,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [status, apnoea, score, accepted, acceptDate]);

    // const updateScore = () => {
    //     let newScore = 0;
    //     if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
    //         newScore = 0;
    //     } else if (status === obsStatuses.patientCondition) {
    //         newScore = 3;
    //     } else {
    //         if (apnoea) {
    //             newScore = apnoea.score;
    //         }
    //     }
    //     setScore(newScore);
    // };
    // useEffect(() => {
    //     updateScore();
    // }, [status, apnoea]);

    const onApnoeaChange = (value) => {
        setApnoea(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) setStatus(value);
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={6}>
                        <AgyleBlockSwitch
                            onChange={value => onApnoeaChange(value)}
                            value={apnoea}
                            customLabels={{
                                no: t('No'),
                                yes: t('Yes'),
                            }}
                        />
                    </Grid>

                    {renderAcceptSection({
                        classes,
                        t,
                        hideScore: true,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: _.isNull(apnoea),
                        showStatusNR: type === obsTypes.pews,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsApnoea.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    apnoea: PropTypes.oneOf([null, false, true]),
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};

ObsApnoea.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsApnoea);
